import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import {
  getIdentityGraphStats,
  triggerIdentityGraphSnapshot,
  getIdentityGraphAttributeFill,
  getIdentityGraphFieldMatrix,
  getEnrichmentDeadLetter,
  rerunEnrichmentDeadLetter,
  IdentityGraphStats,
  IdentityGraphAttributeFill,
  FieldMatrix,
  EnrichDeadLetterResponse,
} from '../../services/auth';
import { canSeeIdentityGraph } from '../../utils/access';

const fmt = (n: number) => (n ?? 0).toLocaleString();
const pctStr = (n: number) => `${(n ?? 0).toFixed(1)}%`;

function StatCard({ label, value, sub, accent }: { label: string; value: string; sub?: React.ReactNode; accent?: string }) {
  return (
    <div style={{
      background: 'var(--card-bg, #1a1d24)',
      border: '1px solid var(--border, #2a2f3a)',
      borderRadius: 10,
      padding: '16px 18px',
      minWidth: 0,
    }}>
      <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#9ca3af' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, marginTop: 6, color: accent || '#f3f4f6' }}>{value}</div>
      {sub != null && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

export default function IdentityGraph() {
  const { user, getToken } = useAuth();
  // Same rule as the nav link and the backend middleware: super_admin from any
  // org, or an org_admin inside the HQ org. NOT any other org's admin.
  const hasAccess = canSeeIdentityGraph(user);

  const [period, setPeriod] = useState<'month' | 'week'>('month');
  const [stats, setStats] = useState<IdentityGraphStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [snapshotting, setSnapshotting] = useState(false);
  const [fill, setFill] = useState<IdentityGraphAttributeFill | null>(null);
  const [matrix, setMatrix] = useState<FieldMatrix | null>(null);
  const [mGroup, setMGroup] = useState<string>('all');
  const [mSource, setMSource] = useState<string>('all');
  const [mFlagged, setMFlagged] = useState<boolean>(false);
  const [mQuery, setMQuery] = useState<string>('');
  const [dlq, setDlq] = useState<EnrichDeadLetterResponse | null>(null);
  const [rerunning, setRerunning] = useState<string | null>(null);

  const loadFill = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) {return;}
      const data = await getIdentityGraphAttributeFill(token);
      setFill(data);
    } catch {
      // Non-fatal: the coverage section just stays hidden on error.
      setFill(null);
    }
  }, [getToken]);

  const loadMatrix = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) {return;}
      const data = await getIdentityGraphFieldMatrix(token);
      setMatrix(data);
    } catch {
      // Non-fatal: the matrix section just stays hidden on error (e.g. the
      // table doesn't exist yet pre-migration).
      setMatrix(null);
    }
  }, [getToken]);

  const loadDlq = useCallback(async () => {
    try {
      const token = await getToken();
      if (!token) {return;}
      const data = await getEnrichmentDeadLetter(token);
      setDlq(data);
    } catch {
      // Non-fatal: the dead-letter panel just stays hidden on error (e.g. the
      // table doesn't exist yet pre-migration).
      setDlq(null);
    }
  }, [getToken]);

  const handleRerun = useCallback(async (leadId: string, dataBodyId: string) => {
    setRerunning(leadId);
    try {
      const token = await getToken();
      if (!token) {return;}
      await rerunEnrichmentDeadLetter(token, leadId, dataBodyId);
      await loadDlq();
    } catch {
      // swallow — the row stays; the admin can retry
    } finally {
      setRerunning(null);
    }
  }, [getToken, loadDlq]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      const data = await getIdentityGraphStats(token, period);
      setStats(data);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to load identity graph stats');
    } finally {
      setLoading(false);
    }
  }, [getToken, period]);

  useEffect(() => { void load(); }, [load]);
  useEffect(() => { void loadFill(); }, [loadFill]);
  useEffect(() => { void loadMatrix(); }, [loadMatrix]);
  useEffect(() => { void loadDlq(); }, [loadDlq]);

  async function handleSnapshot() {
    setSnapshotting(true);
    setError('');
    try {
      const token = await getToken();
      if (!token) { setError('Your session expired. Please sign in again.'); return; }
      await triggerIdentityGraphSnapshot(token);
      await Promise.all([load(), loadFill(), loadMatrix()]);
      // The field-matrix refresh runs in the background (tens of seconds), so
      // re-poll it a couple times to pick up the fresh rows without a reload.
      setTimeout(() => { void loadMatrix(); }, 12000);
      setTimeout(() => { void loadMatrix(); }, 30000);
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Snapshot failed');
    } finally {
      setSnapshotting(false);
    }
  }

  if (!hasAccess) {
    return <div><div className="page-header"><h1 className="page-title">Identity Graph</h1></div><p style={{ color: '#9ca3af' }}>Identity Graph access required.</p></div>;
  }

  const s = stats?.summary;
  const g = stats?.graph;
  const periodLabel = period === 'month' ? 'This Month' : 'Last 7 Days';
  const trend = s ? s.newProfilesChangePct : 0;
  const hasData = !!g && g.lastSnapshotDay != null;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Identity Graph</h1>
          <p className="page-subtitle">
            Owned-enrichment growth monitor{g?.lastSnapshotDay ? ` · snapshot ${g.lastSnapshotDay}` : ''}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <div className="admin-tabs" style={{ margin: 0 }}>
            <button className={`admin-tab ${period === 'month' ? 'active' : ''}`} onClick={() => setPeriod('month')}>Month</button>
            <button className={`admin-tab ${period === 'week' ? 'active' : ''}`} onClick={() => setPeriod('week')}>Week</button>
          </div>
          <button className="btn btn-secondary" onClick={handleSnapshot} disabled={snapshotting} title="Recompute today's totals now (normally runs automatically at 15:00 UTC)">
            {snapshotting ? 'Snapshotting…' : 'Refresh snapshot'}
          </button>
        </div>
      </div>

      {error && <div style={{ background: '#3b1d1d', border: '1px solid #7f1d1d', color: '#fca5a5', padding: '10px 14px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {loading && !stats && <p style={{ color: '#9ca3af' }}>Loading…</p>}

      {!loading && !hasData && (
        <div style={{ background: 'var(--card-bg, #1a1d24)', border: '1px solid var(--border, #2a2f3a)', borderRadius: 10, padding: 24 }}>
          <p style={{ marginTop: 0 }}>No snapshot has been taken yet.</p>
          <p style={{ color: '#9ca3af' }}>
            The snapshot runs automatically each day at 15:00 UTC. To populate it now, click <strong>Refresh snapshot</strong> above.
          </p>
        </div>
      )}

      {stats && hasData && g && s && (
        <>
          {/* Headline graph size */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 12 }}>
            <StatCard label="Total Profiles" value={`≈ ${fmt(g.totalProfiles)}`} accent="#34d399"
              sub={g.profilesAddedSinceBaseline > 0
                ? `live estimate · +${fmt(g.profilesAddedSinceBaseline)} since baseline`
                : 'live estimate (CSV + live capture)'} />
            <StatCard label="Lookup Keys" value={`≈ ${fmt(g.totalKeysApprox)}`}
              sub="live estimate" />
            <StatCard label={`New Profiles · ${periodLabel}`} value={fmt(s.newProfiles)} accent="#60a5fa"
              sub={<span style={{ color: trend >= 0 ? '#34d399' : '#f87171' }}>
                {trend >= 0 ? '▲' : '▼'} {pctStr(Math.abs(trend))} vs prev ({fmt(s.prevPeriodNewProfiles)})
              </span>} />
            <StatCard label={`Leads Processed · ${periodLabel}`} value={fmt(s.rowsProcessed)}
              sub={`${s.filesProcessed} file(s), ${s.runs} run(s)`} />
          </div>

          {/* Quality / efficiency */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12, marginBottom: 20 }}>
            <StatCard label="Duplicates (merged)" value={fmt(s.duplicates)} sub={`${pctStr(s.dedupRatePct)} of processed`} />
            <StatCard label="New Rate" value={pctStr(s.newRatePct)} sub="processed that were brand-new" />
            <StatCard label="Avg New / Active Day" value={fmt(s.avgNewPerActiveDay)} sub={`${s.daysWithActivity} active day(s)`} />
            <StatCard label="Failed Rows" value={fmt(s.failed)} accent={s.failed > 0 ? '#f87171' : undefined} sub={`${pctStr(s.failRatePct)} of processed`} />
          </div>

          {/* Daily breakdown */}
          <div className="admin-table-container">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Day (UTC)</th>
                  <th style={{ textAlign: 'right' }}>Processed</th>
                  <th style={{ textAlign: 'right' }}>New</th>
                  <th style={{ textAlign: 'right' }}>Duplicates</th>
                  <th style={{ textAlign: 'right' }}>Failed</th>
                  <th style={{ textAlign: 'right' }}>Total Profiles (est.)</th>
                </tr>
              </thead>
              <tbody>
                {stats.daily.length === 0 && (
                  <tr><td colSpan={6} style={{ color: '#9ca3af' }}>No activity in this period.</td></tr>
                )}
                {[...stats.daily].reverse().map(d => (
                  <tr key={d.day}>
                    <td>{d.day}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(d.rowsProcessed)}</td>
                    <td style={{ textAlign: 'right', color: d.newProfiles > 0 ? '#34d399' : 'inherit' }}>{fmt(d.newProfiles)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(d.duplicates)}</td>
                    <td style={{ textAlign: 'right', color: d.failed > 0 ? '#f87171' : 'inherit' }}>{fmt(d.failed)}</td>
                    <td style={{ textAlign: 'right' }}>{fmt(d.totalProfiles)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {fill && fill.categories.length > 0 && (
        <div style={{ marginTop: 28 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px' }}>Attribute Coverage (estimated)</h2>
          <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 16px' }}>
            Estimated from a daily sample (N≈{fmt(fill.sampleN)})
            {fill.computedAt ? ` · updated ${new Date(fill.computedAt).toLocaleString()}` : ' · not yet computed'}
          </p>

          {fill.computedAt == null && (
            <div style={{ background: 'var(--card-bg, #1a1d24)', border: '1px solid var(--border, #2a2f3a)', borderRadius: 10, padding: 16, marginBottom: 16, color: '#9ca3af', fontSize: 13 }}>
              The coverage sample runs with the daily 15:00 UTC snapshot. Until it
              first runs, every attribute shows 0%.
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: 16 }}>
            {fill.categories.map(cat => (
              <div key={cat.group} style={{
                background: 'var(--card-bg, #1a1d24)',
                border: '1px solid var(--border, #2a2f3a)',
                borderRadius: 10,
                padding: '14px 16px',
              }}>
                <div style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: 0.5, color: '#9ca3af', marginBottom: 10 }}>
                  {cat.label}
                </div>
                {cat.attributes.map(a => (
                  <div key={a.key} style={{ marginBottom: 9 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 3 }}>
                      <span style={{ color: '#e5e7eb' }}>{a.label}</span>
                      <span style={{ color: '#9ca3af', fontVariantNumeric: 'tabular-nums' }}>{(a.pct ?? 0).toFixed(1)}%</span>
                    </div>
                    <div style={{ height: 6, background: 'var(--border, #2a2f3a)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{
                        width: `${Math.max(0, Math.min(100, a.pct ?? 0))}%`,
                        height: '100%',
                        background: (a.pct ?? 0) > 0 ? '#60a5fa' : 'transparent',
                        borderRadius: 4,
                      }} />
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {matrix && matrix.rows.length > 0 && (() => {
        const groups = Array.from(new Set(matrix.rows.map(r => r.group)));
        const q = mQuery.trim().toLowerCase();
        const rows = matrix.rows.filter(r =>
          (mGroup === 'all' || r.group === mGroup) &&
          (mSource === 'all' || r.source === mSource) &&
          (!mFlagged || r.flags.length > 0) &&
          (!q || r.label.toLowerCase().includes(q) || r.attribute.toLowerCase().includes(q) || r.theirField.toLowerCase().includes(q))
        );
        const flaggedCount = matrix.rows.filter(r => r.flags.length > 0).length;
        const exportCsv = () => {
          const head = ['Group','Attribute','Their Field','Partner','Fill %','Normalizer','Rule Accepts','Rule Drops','Raw Examples','Normalized Values','Flags'];
          const esc = (s: any) => `"${String(s ?? '').replace(/"/g, '""')}"`;
          const lines = [head.map(esc).join(',')];
          rows.forEach(r => lines.push([
            r.group, r.label, r.theirField, r.sourceLabel, r.fillPct, r.normalizer, r.accepts, r.drops,
            r.rawExamples.join(' | '),
            r.valueDist.map(v => `${v.v}:${v.n}`).join(' | '), r.flags.join(' ; '),
          ].map(esc).join(',')));
          const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
          const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
          a.download = `field-translation-matrix_${matrix.snapshotDate || 'latest'}.csv`; a.click();
        };
        const flagColor = (f: string) => f.startsWith('ALL-ONE-VALUE') || f.startsWith('DIVERGENT') ? '#f87171' : f.startsWith('Drift') || f.startsWith('New value') ? '#f59e0b' : '#9ca3af';
        return (
          <div style={{ marginTop: 32 }}>
            <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 4px' }}>Field Translation Matrix</h2>
            <p style={{ fontSize: 12, color: '#9ca3af', margin: '0 0 12px' }}>
              What each partner sends per attribute, the rule we apply, and the real sampled values — so a partner change or a silent normalizer drop is caught on sight.
              {matrix.snapshotDate ? ` · snapshot ${matrix.snapshotDate}` : ''}
              {matrix.prevSnapshotDate ? ` · drift vs ${matrix.prevSnapshotDate}` : ''}
              {flaggedCount > 0 ? ` · ` : ''}
              {flaggedCount > 0 && <span style={{ color: '#f59e0b' }}>{flaggedCount} flagged</span>}
            </p>

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
              <select value={mGroup} onChange={e => setMGroup(e.target.value)} className="form-input" style={{ width: 'auto', padding: '6px 10px' }}>
                <option value="all">All groups</option>
                {groups.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
              <select value={mSource} onChange={e => setMSource(e.target.value)} className="form-input" style={{ width: 'auto', padding: '6px 10px' }}>
                <option value="all">All partners</option>
                {matrix.sources.map(s => <option key={s.source} value={s.source}>{s.label}</option>)}
              </select>
              <input placeholder="Search attribute / field…" value={mQuery} onChange={e => setMQuery(e.target.value)} className="form-input" style={{ width: 220, padding: '6px 10px' }} />
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--text-dark, #1f2937)', cursor: 'pointer' }}>
                <input type="checkbox" checked={mFlagged} onChange={e => setMFlagged(e.target.checked)} /> Flagged only
              </label>
              <span style={{ flex: 1 }} />
              <button className="btn btn-secondary" onClick={exportCsv}>Export CSV</button>
            </div>

            <div style={{ overflowX: 'auto', border: '1px solid var(--border, #2a2f3a)', borderRadius: 10, background: 'var(--card-bg, #1a1d24)', color: '#e5e7eb' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, minWidth: 1100, color: '#e5e7eb' }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: '#9ca3af', background: 'var(--card-bg, #1a1d24)' }}>
                    <th style={{ padding: '8px 10px' }}>Attribute</th>
                    <th style={{ padding: '8px 10px' }}>Partner</th>
                    <th style={{ padding: '8px 10px' }}>Their Field</th>
                    <th style={{ padding: '8px 10px', textAlign: 'right' }}>Fill %</th>
                    <th style={{ padding: '8px 10px' }}>Raw values they send</th>
                    <th style={{ padding: '8px 10px' }}>Normalizes to</th>
                    <th style={{ padding: '8px 10px' }}>Rule</th>
                    <th style={{ padding: '8px 10px' }}>Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.length === 0 && <tr><td colSpan={8} style={{ padding: '10px', color: '#9ca3af' }}>No rows match these filters.</td></tr>}
                  {rows.map(r => (
                    <tr key={`${r.source}:${r.attribute}`} style={{ borderTop: '1px solid #2a2f3a', color: '#e5e7eb', background: r.flags.some(f => f.startsWith('ALL-ONE-VALUE') || f.startsWith('DIVERGENT')) ? 'rgba(248,113,113,0.07)' : undefined }}>
                      <td style={{ padding: '8px 10px' }}>
                        <div style={{ fontWeight: 600 }}>{r.label}</div>
                        <div style={{ color: '#6b7280', fontSize: 11 }}>{r.group} · {r.attribute}</div>
                      </td>
                      <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>{r.sourceLabel}</td>
                      <td style={{ padding: '8px 10px', fontFamily: 'monospace', fontSize: 11.5, color: '#93c5fd' }}>{r.theirField || '—'}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {r.fillPct.toFixed(1)}%
                        {r.pctDelta != null && Math.abs(r.pctDelta) >= 0.1 && (
                          <span style={{ marginLeft: 4, fontSize: 11, color: r.pctDelta >= 0 ? '#34d399' : '#f87171' }}>
                            {r.pctDelta >= 0 ? '▲' : '▼'}{Math.abs(r.pctDelta)}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '8px 10px', maxWidth: 220 }}>
                        {r.rawExamples.length ? r.rawExamples.map((v, i) => (
                          <span key={i} style={{ display: 'inline-block', background: '#23272f', borderRadius: 4, padding: '1px 6px', margin: '1px 3px 1px 0', fontFamily: 'monospace', fontSize: 11 }}>{v}</span>
                        )) : <span style={{ color: '#6b7280' }}>—</span>}
                      </td>
                      <td style={{ padding: '8px 10px', maxWidth: 200 }}>
                        {r.valueDist.length ? r.valueDist.slice(0, 5).map((v, i) => (
                          <span key={i} style={{ color: '#e5e7eb', fontSize: 11.5, marginRight: 8, whiteSpace: 'nowrap' }}>
                            <span style={{ fontFamily: 'monospace' }}>{v.v}</span>
                            <span style={{ color: '#6b7280' }}> ×{v.n}</span>
                          </span>
                        )) : <span style={{ color: '#6b7280' }}>{r.output}</span>}
                      </td>
                      <td style={{ padding: '8px 10px', maxWidth: 260 }} title={`Accepts: ${r.accepts}\nDrops: ${r.drops}`}>
                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#c4b5fd' }}>{r.normalizer}</div>
                        <div style={{ color: '#9ca3af', fontSize: 11, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.drops}</div>
                      </td>
                      <td style={{ padding: '8px 10px', maxWidth: 240 }}>
                        {r.flags.length === 0 ? <span style={{ color: '#4b5563' }}>—</span> : r.flags.map((f, i) => (
                          <div key={i} style={{ color: flagColor(f), fontSize: 11, marginBottom: 2 }}>• {f}</div>
                        ))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {matrix.snapshotDate == null && (
              <div style={{ color: '#9ca3af', fontSize: 13, marginTop: 8 }}>
                The matrix refreshes weekly with the snapshot worker. Click <strong>Refresh snapshot</strong> above to populate it now.
              </div>
            )}
          </div>
        );
      })()}

      {dlq && dlq.total > 0 && (
        <div style={{ marginTop: 28 }}>
          <h3 style={{ margin: '0 0 4px', color: '#f3f4f6' }}>
            Enrichment dead-letter
            <span style={{ marginLeft: 8, fontSize: 13, fontWeight: 400, color: '#f59e0b' }}>
              {fmt(dlq.total)} parked
            </span>
          </h3>
          <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 10 }}>
            Leads whose async enrichment exhausted its retry budget. Re-run to try the vendor again with a fresh budget.
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#9ca3af' }}>
                  <th style={{ padding: '6px 8px' }}>Lead</th>
                  <th style={{ padding: '6px 8px' }}>Source</th>
                  <th style={{ padding: '6px 8px' }}>Attempts</th>
                  <th style={{ padding: '6px 8px' }}>Reason</th>
                  <th style={{ padding: '6px 8px' }}>Last attempt</th>
                  <th style={{ padding: '6px 8px' }} />
                </tr>
              </thead>
              <tbody>
                {dlq.items.map(item => (
                  <tr key={`${item.data_body_id}:${item.lead_id}`} style={{ borderTop: '1px solid #2a2f3a', color: '#e5e7eb' }}>
                    <td style={{ padding: '6px 8px', fontFamily: 'monospace' }}>{item.lead_id.slice(0, 8)}</td>
                    <td style={{ padding: '6px 8px' }}>{item.source || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{item.attempts}</td>
                    <td style={{ padding: '6px 8px', maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={item.reason || ''}>{item.reason || '—'}</td>
                    <td style={{ padding: '6px 8px' }}>{new Date(item.last_attempt_at).toLocaleString()}</td>
                    <td style={{ padding: '6px 8px' }}>
                      <button
                        className="btn btn-secondary"
                        disabled={rerunning === item.lead_id}
                        onClick={() => handleRerun(item.lead_id, item.data_body_id)}
                      >
                        {rerunning === item.lead_id ? 'Re-running…' : 'Re-run'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
